import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getTeamColors } from '@/utils/teamColors';

interface ESPNAthlete {
  id: string;
  name: string;
  display_name?: string;
  first_name?: string;
  sport: string;
  league?: string;
  position?: string;
  current_team_name?: string;
  current_team_logo?: string;
  headshot_url?: string;
  age?: number;
  height?: string;
  weight?: string;
  jersey_number?: number;
  college?: string;
  [key: string]: any;
}

interface AthleteListViewProps {
  athletes: ESPNAthlete[];
  onAthleteClick: (athlete: ESPNAthlete) => void;
  isCollegiate?: boolean;
  collegiateSportsDisplay?: Record<string, string>;
}

export const AthleteListView: React.FC<AthleteListViewProps> = ({
  athletes,
  onAthleteClick,
  isCollegiate = false,
  collegiateSportsDisplay = {},
}) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[60px]"></TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Sport / Position</TableHead>
          <TableHead>Team</TableHead>
          <TableHead>Age</TableHead>
          <TableHead>Height</TableHead>
          <TableHead>Weight</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {athletes.map((athlete) => {
          const teamColors = getTeamColors(athlete.current_team_name || '', isCollegiate ? 'college' : (athlete.league || ''));
          return (
            <TableRow
              key={athlete.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onAthleteClick(athlete)}
            >
              <TableCell>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={athlete.headshot_url || ''} className="object-cover" />
                  <AvatarFallback
                    className="text-xs text-white font-bold"
                    style={{ background: `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)` }}
                  >
                    {athlete.first_name?.[0] || athlete.name[0]}
                  </AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  {athlete.display_name || athlete.name}
                  {athlete.jersey_number && (
                    <Badge variant="outline" className="text-xs">#{athlete.jersey_number}</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {isCollegiate ? (collegiateSportsDisplay[athlete.sport] || athlete.sport) : (athlete.league || athlete.sport).toUpperCase()}
                {athlete.position && ` • ${athlete.position}`}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {athlete.current_team_logo && (
                    <img src={athlete.current_team_logo} alt="" className="h-4 w-4 object-contain" />
                  )}
                  <span className="text-sm">{athlete.current_team_name || '—'}</span>
                </div>
              </TableCell>
              <TableCell>{athlete.age || '—'}</TableCell>
              <TableCell>{athlete.height || '—'}</TableCell>
              <TableCell>{athlete.weight || '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
